// src/components/BizEditModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Loader2, MapPin } from 'lucide-react';

export default function BizEditModal({ isOpen, onClose, onSave, isUpdating, selectedData }) {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (selectedData) {
      setFormData({
        bizRegNo: selectedData.bizRegNo || '',
        bizMgtNo: selectedData.bizMgtNo || '',
        bizName: selectedData.bizName || '',
        ceoName: selectedData.ceoName || '',
        employeeCount: selectedData.employeeCount || '',
        zipcode: selectedData.zipcode || '',
        address1: selectedData.address1 || '',
        address2: selectedData.address2 || '',
        bizPhone: selectedData.bizPhone || '',
        managerName: selectedData.managerName || '',
        regBranch: selectedData.regBranch || '',
      });
    }
  }, [selectedData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddressSearch = () => {
    if (window.daum && window.daum.Postcode) {
      new window.daum.Postcode({
        oncomplete: function (data) {
          setFormData(prev => ({
            ...prev,
            zipcode: data.zonecode,
            address1: data.address
          }));
        }
      }).open();
    } else {
      alert("주소 검색 스크립트를 로드할 수 없습니다.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200">

        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            사업장 정보 수정
          </h3>
          <button onClick={onClose} disabled={isUpdating} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">

            {/* 🌟 수정: 사업자등록번호 & 관리번호를 나란히 배치 */}
            <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-4 mb-2">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">사업자등록번호 (수정불가)</label>
                <input type="text" value={selectedData?.bizRegNo || ''} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">사업장관리번호 (수정불가)</label>
                <input type="text" value={selectedData?.bizMgtNo || ''} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 cursor-not-allowed" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">사업장명 <span className="text-red-500">*</span></label>
                <input type="text" name="bizName" value={formData.bizName} onChange={handleChange} required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">등록지사</label>
                <input type="text" name="regBranch" value={formData.regBranch} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">대표자명</label>
                <input type="text" name="ceoName" value={formData.ceoName} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">근로자수 (명)</label>
                <input type="number" name="employeeCount" value={formData.employeeCount} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">담당자명</label>
                <input type="text" name="managerName" value={formData.managerName} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">현장 연락처</label>
                <input type="text" name="bizPhone" value={formData.bizPhone} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>

            {/* 🌟 주소 입력 영역을 MemberFormModal 스타일로 변경 */}
            <div className="pt-2 border-t border-slate-100 mt-2">
              <label className="block text-xs font-semibold text-slate-500 mb-2">주소 정보</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input 
                    type="text" name="zipcode" readOnly value={formData.zipcode} placeholder="우편번호" 
                    className="h-10 w-28 rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                  />
                  <button 
                    type="button" onClick={openAddressSearch} 
                    className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> 주소 검색
                  </button>
                </div>
                <input 
                  type="text" name="address1" readOnly value={formData.address1} placeholder="기본 주소 (주소 검색을 이용하세요)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                />
                <input 
                  type="text" name="address2" value={formData.address2} onChange={handleChange} placeholder="상세 주소를 입력하세요 (본사, 1동 등)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" 
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 rounded-b-2xl bg-slate-50 px-6 py-4 border-t border-slate-100">
            <button type="button" onClick={onClose} disabled={isUpdating} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors">
              취소
            </button>
            <button type="submit" disabled={isUpdating} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isUpdating ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}